// Push Notification Service
import config from "../config";
import { getStoredToken } from "../utils/api";
// Handles subscription, unsubscription, and sending push notifications

const VAPID_PUBLIC_KEY = config.VAPID_PUBLIC_KEY || process.env.REACT_APP_VAPID_PUBLIC_KEY || '';

// Convert VAPID key from base64 to Uint8Array
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

class PushNotificationService {
  constructor() {
    this.registration = null;
    this.subscription = null;
  }

  isServiceWorkerContextReady() {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return false;
    }
    if (!window.isSecureContext) {
      return false;
    }
    return !document.prerendering;
  }

  async waitForServiceWorkerContextReady(timeoutMs = 15000) {
    if (this.isServiceWorkerContextReady()) {
      return true;
    }

    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return false;
    }

    return new Promise((resolve) => {
      let done = false;

      const cleanup = () => {
        document.removeEventListener('visibilitychange', onReadyCheck);
        window.removeEventListener('focus', onReadyCheck);
        window.removeEventListener('pageshow', onReadyCheck);
      };

      const finish = (value) => {
        if (done) return;
        done = true;
        cleanup();
        resolve(value);
      };

      const onReadyCheck = () => {
        if (this.isServiceWorkerContextReady()) {
          finish(true);
        }
      };

      document.addEventListener('visibilitychange', onReadyCheck);
      window.addEventListener('focus', onReadyCheck);
      window.addEventListener('pageshow', onReadyCheck);

      setTimeout(() => finish(this.isServiceWorkerContextReady()), timeoutMs);
      onReadyCheck();
    });
  }

  // Check if service workers and push notifications are supported
  isSupported() {
    return (
      'serviceWorker' in navigator &&
      'PushManager' in window &&
      'Notification' in window
    );
  }

  // Check notification permission status
  async getPermissionStatus() {
    if (!('Notification' in window)) {
      return 'not-supported';
    }
    return Notification.permission;
  }

  // Request notification permission
  async requestPermission() {
    if (!('Notification' in window)) {
      throw new Error('Notifications are not supported in this browser');
    }

    const permission = await Notification.requestPermission();
    return permission;
  }

  // Register service worker
  async registerServiceWorker() {
    if (!('serviceWorker' in navigator)) {
      throw new Error('Service Workers are not supported in this browser');
    }

    if (!this.isServiceWorkerContextReady()) {
      const becameReady = await this.waitForServiceWorkerContextReady();
      if (!becameReady) {
        throw new Error('Service Worker APIs are temporarily unavailable until this tab becomes active.');
      }
    }

    for (let attempt = 1; attempt <= 2; attempt += 1) {
      try {
        this.registration = await navigator.serviceWorker.register('/sw.js', {
          scope: '/'
        });
        console.log('Service Worker registered:', this.registration);
        return this.registration;
      } catch (error) {
        if (error?.name === 'InvalidStateError' && attempt === 1) {
          await this.waitForServiceWorkerContextReady();
          continue;
        }
        console.error('Service Worker registration failed:', error);
        throw error;
      }
    }

    return null;
  }

  // Subscribe to push notifications
  async subscribe() {
    if (!this.isSupported()) {
      throw new Error('Push notifications are not supported');
    }
    if (!this.isServiceWorkerContextReady()) {
      const becameReady = await this.waitForServiceWorkerContextReady();
      if (!becameReady) {
        throw new Error('Cannot subscribe while the document is not active.');
      }
    }

    // Register service worker if not already registered
    if (!this.registration) {
      await this.registerServiceWorker();
    }

    // Wait for service worker to be ready
    await navigator.serviceWorker.ready;

    // Check if already subscribed
    this.subscription = await this.registration.pushManager.getSubscription();

    if (this.subscription) {
      console.log('Already subscribed:', this.subscription);
      return this.subscription;
    }

    // Request permission if not granted
    const permission = await this.getPermissionStatus();
    if (permission !== 'granted') {
      const newPermission = await this.requestPermission();
      if (newPermission !== 'granted') {
        if (newPermission === 'denied') {
          throw new Error('Notification permission was denied. Please enable it in your browser settings.');
        } else {
          throw new Error('Notification permission not granted');
        }
      }
    }

    // Subscribe to push notifications
    try {
      // Check if VAPID key is available
      if (!VAPID_PUBLIC_KEY || VAPID_PUBLIC_KEY.trim() === '') {
        throw new Error('VAPID public key is not configured. Please add REACT_APP_VAPID_PUBLIC_KEY to your .env file. For now, you can still receive in-app notifications.');
      }

      const applicationServerKey = urlBase64ToUint8Array(VAPID_PUBLIC_KEY);

      this.subscription = await this.registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: applicationServerKey
      });

      console.log('Subscribed to push notifications:', this.subscription);

      // Send subscription to backend
      await this.sendSubscriptionToBackend(this.subscription);

      return this.subscription;
    } catch (error) {
      console.error('Subscription failed:', error);
      throw error;
    }
  }

  // Unsubscribe from push notifications
  async unsubscribe() {
    try {
      if (!this.isServiceWorkerContextReady()) {
        return false;
      }

      // Ensure service worker is registered
      if (!this.registration) {
        await this.registerServiceWorker();
      }

      // Wait for service worker to be ready
      await navigator.serviceWorker.ready;

      // Get current subscription
      if (!this.subscription) {
        this.subscription = await this.registration.pushManager.getSubscription();
      }

      if (this.subscription) {
        const successful = await this.subscription.unsubscribe();
        if (successful) {
          console.log('Unsubscribed from push notifications');
          // Notify backend (don't wait for it)
          this.removeSubscriptionFromBackend(this.subscription).catch(err => {
            console.warn('Failed to notify backend of unsubscribe:', err);
          });
          this.subscription = null;
          return true;
        } else {
          console.warn('Unsubscribe returned false');
          return false;
        }
      } else {
        // No subscription found, already unsubscribed
        console.log('No subscription found, already unsubscribed');
        return true;
      }
    } catch (error) {
      console.error('Error unsubscribing from push notifications:', error);
      // Even if unsubscribe fails, clear the subscription reference
      this.subscription = null;
      throw error;
    }
  }

  // Get current subscription
  async getSubscription() {
    if (!this.isServiceWorkerContextReady()) {
      return null;
    }

    if (!this.registration) {
      await this.registerServiceWorker();
    }

    await navigator.serviceWorker.ready;
    this.subscription = await this.registration.pushManager.getSubscription();
    return this.subscription;
  }

  // Send subscription to backend
  async sendSubscriptionToBackend(subscription) {
    try {
      const token = getStoredToken();
      
      // Extract keys properly from PushSubscription object
      // The getKey() method returns the keys as ArrayBuffer, convert to base64
      const convertArrayBufferToBase64 = (buffer) => {
        const bytes = new Uint8Array(buffer);
        let binary = '';
        for (let i = 0; i < bytes.byteLength; i++) {
          binary += String.fromCharCode(bytes[i]);
        }
        return window.btoa(binary);
      };

      const subscriptionData = {
        endpoint: subscription.endpoint,
        keys: {
          auth: convertArrayBufferToBase64(subscription.getKey('auth')),
          p256dh: convertArrayBufferToBase64(subscription.getKey('p256dh'))
        }
      };

      const response = await fetch(`${config.API_BASE_URL}/api/push/subscribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify({
          subscription: subscriptionData,
          userAgent: navigator.userAgent,
          timestamp: new Date().toISOString()
        })
      });

      if (!response.ok) {
        throw new Error(`Failed to register push subscription on backend (${response.status})`);
      }

      console.log('Subscription sent to backend successfully');
    } catch (error) {
      console.error('Error sending subscription to backend:', error);
      throw error;
    }
  }

  // Remove subscription from backend
  async removeSubscriptionFromBackend(subscription) {
    try {
      const token = getStoredToken();
      await fetch(`${config.API_BASE_URL}/api/push/unsubscribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify({
          subscription: {
            endpoint: subscription.endpoint
          }
        })
      });
    } catch (error) {
      console.error('Error removing subscription from backend:', error);
    }
  }

  // Send a test notification (for development)
  async sendTestNotification() {
    if (!this.isServiceWorkerContextReady()) {
      throw new Error('Cannot show a notification while the document is not active.');
    }

    if (!this.registration) {
      await this.registerServiceWorker();
    }

    await navigator.serviceWorker.ready;

    await this.registration.showNotification('Test Notification', {
      body: 'This is a test notification from SambhavPro',
      icon: '/logo192.png',
      badge: '/logo192.png',
      tag: 'test-notification',
      requireInteraction: false,
      vibrate: [200, 100, 200]
    });
  }
}

// Export singleton instance
const pushNotificationService = new PushNotificationService();
export default pushNotificationService;

